import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as CalendarIcon } from 'assets/icons/trial-miner-icons/calendar-blank.svg';
import { ReactComponent as ChartIcon } from 'assets/icons/trial-miner-icons/chart.svg';
import { ReactComponent as LightningIcon } from 'assets/icons/trial-miner-icons/lightning.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/trial-miner-icons/question.svg';
import S21 from 'assets/miner-icons/S21.png';
import { useInnerWidth } from 'hooks/useInnerWidth';
import { openHowTrialWorksPopupAction } from 'store/modals-reducer/modals.reducer';
import { selectProfile } from 'store/profile-reducer/profile.selectors';
import { requestProfileDataThunkAction } from 'store/profile-reducer/profile.thunk-actions';
import { selectActiveMiners, selectTrialMiners } from 'store/purchase-reducer/purchase.selectors';
import {
  fetchActiveMinersThunkAction,
  fetchActiveTrialMinersAction,
} from 'store/purchase-reducer/purchase.thunk-actions';
import { useAppDispatch } from 'store/store';
import { BreakPoint } from 'styles/style-variables/breakpoint';

import { useCart } from 'components/logic/Cart/hooks/useCart';
import { gtmClickStartTrial } from 'utils/gtmSender/gtmSender';
import { AppRoute } from 'utils/route/app-route';

import * as S from './TrialMinerSection.styled';

export const TrialMinerSection: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { innerWidth } = useInnerWidth();
  const { t } = useTranslation();
  const minersData = useSelector(selectActiveMiners);
  const trialMinersData = useSelector(selectTrialMiners);
  const { hasTrial } = useSelector(selectProfile);

  const {
    minersInCartLength,
    trialMinersInCartLength,
    handleCheckoutClick,
    handleTrialCheckoutClick,
  } = useCart();
  const isMobile = innerWidth <= parseInt(BreakPoint.MobileTop);

  const isTrialMinerInCart = trialMinersInCartLength && trialMinersInCartLength > 0;

  const handleStartTrialButtonClick = () => {
    const isTrialMinerId = minersData
      .concat(trialMinersData)
      .find((miner) => miner.model.toLowerCase().includes('trial'))?.id;

    if (!isTrialMinerId) return;

    const gtmTrialClick = () => {
      const isHomePage = location.pathname === AppRoute.Home();
      const isMinersPage = location.pathname === AppRoute.Purchase();
      gtmClickStartTrial(
        isHomePage
          ? 'start_trial_banner_dashboard_button'
          : (isMinersPage && 'start_trial_banner_miners_page_button') || null,
      );
    };

    if ((minersInCartLength ?? 0) > 0 && isTrialMinerInCart) {
      gtmTrialClick();
      handleCheckoutClick();
    } else {
      gtmTrialClick();
      handleTrialCheckoutClick();
    }
  };

  const handleHowItWorksButtonClick = () => {
    dispatch(openHowTrialWorksPopupAction());
  };

  useEffect(() => {
    void dispatch(fetchActiveMinersThunkAction());
    void dispatch(fetchActiveTrialMinersAction());
    void dispatch(requestProfileDataThunkAction());
  }, [dispatch]);

  if (hasTrial === undefined || hasTrial) {
    return null;
  }

  return (
    <S.TrialMinerSection>
      <S.TrialMinerSectionCard>
        <S.CardContent>
          <S.TrialMinerTittleWrapper>
            <S.TrialMinerTittle>{t('trialMiner.minerNameText')}</S.TrialMinerTittle>
            <S.BadgesWrapper>
              <S.Badge>
                <ChartIcon />
                <S.TextBadge>{t('trialMiner.roiText')}</S.TextBadge>
              </S.Badge>
              <S.Badge>
                <CalendarIcon />
                <S.TextBadge>{t('trialMiner.trialTimeText')}</S.TextBadge>
              </S.Badge>
              <S.Badge>
                <LightningIcon />
                <S.TextBadge>{t('trialMiner.electricityText')}</S.TextBadge>
              </S.Badge>
            </S.BadgesWrapper>
          </S.TrialMinerTittleWrapper>
          <S.ButtonWrapper>
            <S.StartTrialButton
              onClick={handleStartTrialButtonClick}
              disabled={hasTrial}
              text={
                <S.StartTrialText>
                  {t('trialMiner.startTrialNowText')}{' '}
                  <S.StartTrialTextBold>{t('trialMiner.trialPriceText')}</S.StartTrialTextBold>
                </S.StartTrialText>
              }
            />
            <S.HowItWorksButton
              text={t('trialMiner.howItWorksText')}
              onClick={handleHowItWorksButtonClick}
              icon={<QuestionIcon />}
              svgProps={{
                svgFill: 'transparent',
              }}
              variant={isMobile ? 'borderless' : 'primary'}
            />
          </S.ButtonWrapper>
        </S.CardContent>

        <S.MinerIconWrapper>{<img src={S21} alt="" />}</S.MinerIconWrapper>
      </S.TrialMinerSectionCard>
    </S.TrialMinerSection>
  );
};
